import { useState } from "react";
import { useNossaHistoria, TimelineEvent } from "@/hooks/useNossaHistoria";
import { nossaHistoriaApi } from "@/integrations/mysql/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Trash2, Edit2, Plus, Save, X, GripVertical, Eye, EyeOff } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function SortableEvent({ event, onEdit, onDelete, onToggle }: {
  event: TimelineEvent;
  onEdit: (e: TimelineEvent) => void;
  onDelete: (id: string) => void;
  onToggle: (e: TimelineEvent) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: event.id });
  return (
    <Card ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing mt-0.5 text-gray-400 hover:text-gray-600 shrink-0">
            <GripVertical className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-bold bg-brand-primary/10 text-brand-primary px-2.5 py-0.5 rounded-full shrink-0">{event.year}</span>
              <h4 className="font-semibold text-sm text-gray-800 truncate">{event.title}</h4>
              {!event.is_active && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded shrink-0">Inativo</span>}
            </div>
            <p className="text-xs text-gray-500 line-clamp-2">{event.description}</p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button onClick={() => onToggle(event)} className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${event.is_active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
              {event.is_active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
              {event.is_active ? "Visível" : "Oculto"}
            </button>
            <Button size="sm" variant="outline" onClick={() => onEdit(event)} className="h-7 w-7 p-0"><Edit2 className="w-3.5 h-3.5" /></Button>
            <Button size="sm" variant="outline" onClick={() => { if (confirm('Excluir este evento?')) onDelete(event.id); }} className="h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:border-red-300"><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function TimelineManager() {
  const { timelineEvents, timelineLoading, updateTimelineEvent, createTimelineEvent, deleteTimelineEvent } = useNossaHistoria();
  const [localItems, setLocalItems] = useState<TimelineEvent[] | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<Partial<TimelineEvent>>({ year: '', title: '', description: '', is_active: true, display_order: 0 });
  const { toast } = useToast();

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const displayed = localItems ?? (timelineEvents || []);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const reordered = arrayMove(displayed, displayed.findIndex(e => e.id === active.id), displayed.findIndex(e => e.id === over.id));
    setLocalItems(reordered);
    try {
      await Promise.all(reordered.map((e, i) => nossaHistoriaApi.updateTimeline(e.id, { display_order: i })));
      toast({ title: "Ordem atualizada!" });
    } catch {
      toast({ title: "Erro ao atualizar ordem", variant: "destructive" });
      setLocalItems(null);
    }
  };

  const handleCreate = () => { setIsCreating(true); setEditingId(null); setFormData({ year: new Date().getFullYear().toString(), title: '', description: '', is_active: true, display_order: displayed.length }); };
  const handleEdit = (e: TimelineEvent) => { setEditingId(e.id); setFormData(e); setIsCreating(false); };
  const handleSave = () => {
    if (!formData.year || !formData.title || !formData.description) return;
    if (isCreating) { createTimelineEvent(formData as Omit<TimelineEvent, 'id'>); setIsCreating(false); }
    else if (editingId) { updateTimelineEvent({ id: editingId, updates: formData }); setEditingId(null); }
    setFormData({ year: '', title: '', description: '', is_active: true, display_order: 0 });
    setLocalItems(null);
  };
  const handleCancel = () => { setEditingId(null); setIsCreating(false); setFormData({ year: '', title: '', description: '', is_active: true, display_order: 0 }); };

  if (timelineLoading) return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full" />)}</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={handleCreate} disabled={isCreating || !!editingId}><Plus className="w-4 h-4 mr-2" />Adicionar Evento</Button>
      </div>

      {(isCreating || editingId) && (
        <Card className="border-2 border-brand-primary/30">
          <CardHeader className="pb-3"><CardTitle className="text-base">{isCreating ? 'Novo Evento' : 'Editar Evento'}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-gray-600">Ano</Label>
                <Input className="mt-1" value={formData.year} onChange={e => setFormData(p => ({ ...p, year: e.target.value }))} maxLength={10} placeholder="Ex: 2024" />
              </div>
              <div className="flex items-end gap-2">
                <div className="flex items-center gap-2">
                  <Switch checked={!!formData.is_active} onCheckedChange={v => setFormData(p => ({ ...p, is_active: v }))} />
                  <Label>Visível</Label>
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-gray-600">Título ({formData.title?.length || 0}/200)</Label>
              <Input className="mt-1" value={formData.title} onChange={e => setFormData(p => ({ ...p, title: e.target.value }))} maxLength={200} placeholder="Ex: Formação em Nutrição" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-gray-600">Descrição ({formData.description?.length || 0}/500)</Label>
              <Textarea className="mt-1" value={formData.description} onChange={e => setFormData(p => ({ ...p, description: e.target.value }))} maxLength={500} rows={3} />
            </div>
            <div className="flex gap-2 pt-1">
              <Button onClick={handleSave} disabled={!formData.year || !formData.title || !formData.description}><Save className="w-4 h-4 mr-2" />Salvar</Button>
              <Button onClick={handleCancel} variant="outline"><X className="w-4 h-4 mr-2" />Cancelar</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {displayed.length === 0 ? (
        <div className="text-center py-10 text-gray-400 text-sm">Nenhum evento cadastrado.</div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-gray-400 flex items-center gap-1.5"><GripVertical className="w-3.5 h-3.5" />Arraste para reordenar</p>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={displayed.map(e => e.id)} strategy={verticalListSortingStrategy}>
              {displayed.map(e => (
                <SortableEvent key={e.id} event={e} onEdit={handleEdit}
                  onDelete={id => { deleteTimelineEvent(id); setLocalItems(null); }}
                  onToggle={e => updateTimelineEvent({ id: e.id, updates: { is_active: !e.is_active } })} />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
}
